import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Shield, ArrowLeft, Mail, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  // Forgot password state
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'reset'
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Välkommen tillbaka!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggningen misslyckades');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fel');
      toast.success('Återställningskod skickad till din e-post!');
      setView('reset');
    } catch (err) {
      toast.error(err.message || 'Kunde inte skicka återställningskod');
    } finally {
      setSending(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode.trim() || !newPassword) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotEmail.trim().toLowerCase(),
          code: resetCode.trim(),
          new_password: newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fel');
      toast.success('Lösenordet har återställts! Logga in med ditt nya lösenord.');
      setView('login');
      setFormData({ email: forgotEmail, password: '' });
      setResetCode('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.message || 'Kunde inte återställa lösenordet');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm bg-navy text-white mb-4">
            <Shield className="h-7 w-7" />
          </div>
          <h1 className="font-chivo font-black text-3xl text-navy tracking-tight">
            Vapelarm Cloud
          </h1>
          <p className="text-slate-500 mt-1 font-ibm">
            AI-drivet system för vape-detektion
          </p>
        </div>

        {view === 'login' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-chivo text-xl">Logga in</CardTitle>
              <CardDescription className="font-ibm">
                Ange dina inloggningsuppgifter för att komma åt ditt konto
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-ibm text-xs uppercase tracking-widest text-slate-600">
                    E-post
                  </Label>
                  <Input
                    id="email" type="email" placeholder="admin@skola.se"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required className="bg-slate-50 border-slate-200" data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="font-ibm text-xs uppercase tracking-widest text-slate-600">
                      Lösenord
                    </Label>
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(formData.email); setView('forgot'); }}
                      className="text-xs text-navy hover:underline font-ibm"
                      data-testid="forgot-password-link"
                    >
                      Glömt lösenord?
                    </button>
                  </div>
                  <Input
                    id="password" type="password" placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required className="bg-slate-50 border-slate-200" data-testid="login-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-navy hover:bg-navy/90 rounded-sm font-ibm" disabled={loading} data-testid="login-submit">
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loggar in...</>) : 'Logga in'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {view === 'forgot' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-chivo text-xl flex items-center gap-2">
                <Mail className="h-5 w-5 text-navy" /> Glömt lösenord
              </CardTitle>
              <CardDescription className="font-ibm">
                Ange din e-postadress så skickar vi en återställningskod
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest text-slate-600">E-post</Label>
                  <Input
                    type="email" placeholder="din@email.se"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required className="bg-slate-50 border-slate-200" data-testid="forgot-email"
                  />
                </div>
                <Button type="submit" className="w-full bg-navy hover:bg-navy/90 rounded-sm font-ibm" disabled={sending} data-testid="forgot-submit">
                  {sending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Skickar...</>) : 'Skicka återställningskod'}
                </Button>
              </form>
              <button
                onClick={() => setView('login')}
                className="mt-4 flex items-center gap-1 text-sm text-slate-500 hover:text-navy font-ibm mx-auto"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Tillbaka till inloggning
              </button>
            </CardContent>
          </Card>
        )}

        {view === 'reset' && (
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="font-chivo text-xl flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-navy" /> Nytt lösenord
              </CardTitle>
              <CardDescription className="font-ibm">
                Ange koden du fick via e-post och välj ett nytt lösenord
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest text-slate-600">Återställningskod</Label>
                  <Input
                    type="text" placeholder="123456" maxLength={6}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                    required className="bg-slate-50 border-slate-200 text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="reset-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest text-slate-600">Nytt lösenord</Label>
                  <Input
                    type="password" placeholder="Minst 6 tecken"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required minLength={6} className="bg-slate-50 border-slate-200"
                    data-testid="reset-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-navy hover:bg-navy/90 rounded-sm font-ibm" disabled={sending} data-testid="reset-submit">
                  {sending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Återställer...</>) : 'Återställ lösenord'}
                </Button>
              </form>
              <button
                onClick={() => setView('forgot')}
                className="mt-4 flex items-center gap-1 text-sm text-slate-500 hover:text-navy font-ibm mx-auto"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Skicka ny kod
              </button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
